import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/PageHeader'
import { useBroadcastDevices } from './hooks/useBroadcastDevices'
import { useClinicQueueModuleStats } from './hooks/useClinicQueueModuleStats'
import { useLatestSystemVersion } from '@/hooks/useLatestSystemVersion'
import { useDeviceOnlineStatus } from '@/hooks/useDeviceOnlineStatus'
import DataTable from './components/DataTable'
import { getColumns } from './columns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useViewMode } from '@/stores/viewMode'
import { useDisconnectionAlertDialog } from '@/stores/useDisconnectionAlertDialog'
import { DisconnectionAlertDialog } from '@/components/DisconnectionAlertDialog'
import { AlertScopeType } from '@magnetar/admin-api-interfaces'
import { Bell } from 'lucide-react'

export default function DeviceManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { mode, id: viewModeId } = useViewMode()
  const { openDialog: openAlertDialog } = useDisconnectionAlertDialog()

  // 從 URL query 讀取初始值
  const initialVersionFilter = searchParams.get('versionFilter') === 'outdated' ? 'outdated' : 'all'

  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [versionFilter, setVersionFilter] = useState<'all' | 'outdated'>(initialVersionFilter)

  // When in provider mode, pass providerId to filter devices
  const providerId = mode === 'provider' && viewModeId ? viewModeId : undefined

  const { latestVersion } = useLatestSystemVersion()
  const { devices, pagination, isLoading, isError } = useBroadcastDevices({
    page,
    pageSize,
    search,
    versionMismatch: versionFilter === 'outdated',
    latestVersion: latestVersion?.version,
    providerId,
  })

  // 全部診所「開啟叫號 / 沒開叫號」的數量（顯示於搜尋旁）
  const { enabled: queueEnabledCount, disabled: queueDisabledCount } =
    useClinicQueueModuleStats({ providerId })

  // 收集當前頁面的設備 ID
  const deviceIds = useMemo(() => {
    return devices.map((device) => device.broadcast_device_id)
  }, [devices])

  // 使用通用的設備在線狀態 Hook
  const { onlineDeviceIds, deviceLastConnectedMap } = useDeviceOnlineStatus(deviceIds)

  // 當 versionFilter 改變時，更新 URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams)
    if (versionFilter === 'outdated') {
      newParams.set('versionFilter', 'outdated')
    } else {
      newParams.delete('versionFilter')
    }
    setSearchParams(newParams, { replace: true })
  }, [versionFilter, searchParams, setSearchParams])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleVersionFilterChange = (value: string) => {
    setVersionFilter(value as 'all' | 'outdated')
    setPage(1)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-96 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">載入裝置資料時發生錯誤</p>
        <p className="text-sm text-muted-foreground mt-2">請稍後再試</p>
      </div>
    )
  }

  const columns = getColumns()

  return (
    <div className="space-y-6">
      <PageHeader
        title="裝置管理"
        description="管理所有廣播裝置的版本和狀態"
        level={mode === 'admin' ? 'page' : 'section'}
      >
        <Button variant="outline" onClick={() => openAlertDialog(AlertScopeType.system)}>
          <Bell className="h-4 w-4 mr-2" />
          斷線警示設定
        </Button>
      </PageHeader>

      <div className="flex items-center gap-4">
        <Select value={versionFilter} onValueChange={handleVersionFilterChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="選擇版本篩選" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有裝置</SelectItem>
            <SelectItem value="outdated">版本不符的裝置</SelectItem>
          </SelectContent>
        </Select>
        {latestVersion && (
          <p className="text-sm text-muted-foreground">最新版本: {latestVersion.version}</p>
        )}
      </div>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={devices}
            searchValue={search}
            onSearchChange={handleSearch}
            pagination={{
              page,
              pageSize,
              total: pagination?.total || 0,
              totalPages: pagination?.totalPages || 0,
              onPageChange: handlePageChange,
            }}
            meta={{
              onlineDeviceIds,
              deviceLastConnectedMap,
            }}
            headerExtra={
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">
                  有開叫號：
                  <span className="font-semibold text-emerald-600">{queueEnabledCount}</span>
                </span>
                <span className="text-muted-foreground">
                  沒開叫號：
                  <span className="font-semibold text-foreground">{queueDisabledCount}</span>
                </span>
              </div>
            }
          />
        </CardContent>
      </Card>

      {/* 斷線警示設定對話框 */}
      <DisconnectionAlertDialog />
    </div>
  )
}
